import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const dotVariants = cva("size-2 shrink-0 rounded-full", {
  variants: {
    tone: {
      success: "bg-success",
      warning: "bg-warning",
      info: "bg-info",
      error: "bg-destructive",
      neutral: "bg-neutral",
    },
  },
  defaultVariants: {
    tone: "neutral",
  },
})

type StatusDotProps = React.ComponentProps<"span"> &
  VariantProps<typeof dotVariants> & {
    /** The status text. Always required: the color alone never carries meaning. */
    label: string
    /** Show only the dot; the label stays available to screen readers. */
    hideLabel?: boolean
  }

/**
 * A colored dot followed by a status label ("Active", "At risk", "New").
 * Pick the tone by meaning in domain code: success = healthy / done,
 * warning = in progress / needs attention, info = new, error = blocked / lost,
 * neutral = inactive.
 */
function StatusDot({
  tone,
  label,
  hideLabel = false,
  className,
  ...props
}: StatusDotProps) {
  return (
    <span
      data-slot="status-dot"
      className={cn("inline-flex items-center gap-1.5", className)}
      title={hideLabel ? label : undefined}
      {...props}
    >
      <span aria-hidden="true" className={dotVariants({ tone })} />
      <span className={hideLabel ? "sr-only" : undefined}>{label}</span>
    </span>
  )
}

export { StatusDot }
